<script lang="ts">
	/**
	 * ConsentBanner — mandatory-choice GDPR consent for analytics.
	 *
	 * Renders only when (a) GA is configured AND (b) the visitor is in a
	 * GDPR region AND (c) they haven't yet made a choice. Both buttons
	 * are equally weighted (CNIL: dark patterns are illegal). No close,
	 * no ESC dismissal, no backdrop dismissal.
	 */

	import { onMount } from 'svelte';
	import EkaviMark from './EkaviMark.svelte';
	import { needsConsentDecision, setConsent } from '$lib/analytics';

	let dialogEl: HTMLDialogElement | undefined = $state();
	let show = $state(false);
	let acceptBtn: HTMLButtonElement | undefined = $state();
	let focusTimer: ReturnType<typeof setTimeout> | undefined;

	onMount(() => {
		if (needsConsentDecision()) show = true;
		return () => {
			if (focusTimer !== undefined) clearTimeout(focusTimer);
		};
	});

	$effect(() => {
		if (!dialogEl) return;
		if (show && !dialogEl.open) {
			try {
				dialogEl.showModal();
			} catch {
				return;
			}
			if (focusTimer !== undefined) clearTimeout(focusTimer);
			focusTimer = setTimeout(() => acceptBtn?.focus({ preventScroll: true }), 50);
		} else if (!show && dialogEl.open) {
			try {
				dialogEl.close();
			} catch {
				/* already closed */
			}
		}
	});

	function choose(decision: 'granted' | 'denied') {
		setConsent(decision);
		show = false;
	}

	function blockCancel(e: Event) {
		e.preventDefault();
	}

	function blockEscape(e: KeyboardEvent) {
		if (e.key === 'Escape' && dialogEl?.open) {
			e.preventDefault();
			e.stopPropagation();
		}
	}
</script>

<svelte:window onkeydowncapture={blockEscape} />

<dialog
	bind:this={dialogEl}
	class="consent"
	aria-labelledby="consent-title"
	aria-describedby="consent-body"
	oncancel={blockCancel}
>
	<div class="consent-modal" role="document">
		<header class="consent-head">
			<span class="consent-mark" aria-hidden="true">
				<EkaviMark size={20} decorative maskIdSuffix="consent" />
			</span>
			<p class="consent-eyebrow">From Ekavi · about analytics</p>
		</header>

		<div class="consent-body">
			<h2 id="consent-title" class="consent-title">A small note before we go on.</h2>
			<p id="consent-body" class="consent-text">
				We'd like to see if anyone is reading. No ads, no selling, no AI training. May we turn on
				analytics?
			</p>

			<div class="consent-actions">
				<button
					type="button"
					class="consent-btn consent-btn-primary"
					onclick={() => choose('granted')}
					bind:this={acceptBtn}
				>
					OK
				</button>
				<button
					type="button"
					class="consent-btn consent-btn-ghost"
					onclick={() => choose('denied')}
				>
					Decline
				</button>
			</div>

			<p class="consent-fine">
				Either way, nothing is sold or shared. <a href="/privacy/">Read the privacy note</a>
			</p>
		</div>
	</div>
</dialog>

<style>
	.consent {
		border: none;
		padding: 0;
		background: transparent;
		max-width: none;
		max-height: none;
		color: inherit;
		margin: auto;
	}
	.consent::backdrop {
		background: rgba(26, 27, 30, 0.55);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
	}
	.consent-modal {
		width: min(440px, calc(100vw - 32px));
		background: var(--ekavi-bone);
		border: var(--ekavi-stroke-hairline);
		display: flex;
		flex-direction: column;
		overflow: hidden;
		position: relative;
	}
	.consent[open] .consent-modal {
		animation: consent-in 200ms var(--ekavi-ease-out);
	}
	@keyframes consent-in {
		from {
			opacity: 0;
			transform: translateY(8px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}

	.consent-head {
		display: flex;
		align-items: center;
		gap: var(--ekavi-space-2);
		padding: var(--ekavi-space-3) var(--ekavi-space-6);
		border-bottom: var(--ekavi-stroke-hairline);
		background: var(--ekavi-paper);
	}
	.consent-mark {
		color: var(--ekavi-charcoal);
		line-height: 0;
	}
	.consent-eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		margin: 0;
		font-weight: var(--ekavi-weight-halbfett);
	}

	.consent-body {
		padding: var(--ekavi-space-6);
	}
	.consent-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-20);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-3);
		line-height: var(--ekavi-leading-headers);
	}
	.consent-text {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		line-height: var(--ekavi-leading-body);
	}

	.consent-actions {
		display: flex;
		gap: var(--ekavi-space-2);
		margin-bottom: var(--ekavi-space-4);
	}
	.consent-btn {
		appearance: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: 1;
		height: 42px;
		padding: 0 var(--ekavi-space-4);
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		border: 1px solid transparent;
		cursor: pointer;
		transition:
			background var(--ekavi-duration-quick) var(--ekavi-ease-out),
			color var(--ekavi-duration-quick) var(--ekavi-ease-out),
			border-color var(--ekavi-duration-quick) var(--ekavi-ease-out);
	}
	.consent-btn-primary {
		background: var(--ekavi-charcoal);
		color: var(--ekavi-bone);
		border-color: var(--ekavi-charcoal);
	}
	.consent-btn-primary:hover {
		opacity: 0.88;
	}
	.consent-btn-ghost {
		background: transparent;
		color: var(--ekavi-ink-2);
		border-color: var(--ekavi-hairline);
	}
	.consent-btn-ghost:hover {
		color: var(--ekavi-ink-1);
		border-color: var(--ekavi-charcoal);
	}
	.consent-btn:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 3px;
	}
	.consent-fine {
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: 0;
	}
	.consent-fine a {
		color: var(--ekavi-ink-2);
	}
	.consent-fine a:hover {
		color: var(--ekavi-ink-1);
	}

	@media (prefers-reduced-motion: reduce) {
		.consent[open] .consent-modal {
			animation: none;
		}
		.consent-btn,
		.consent-fine a {
			transition: none;
		}
	}
</style>
